import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useSignIn, useAuth, useUser } from '@clerk/clerk-react';
import { useState, useCallback, useEffect, useRef } from 'react';
import { track } from '@/lib/analytics';
import { getPostAuthDestination } from '@/lib/postAuthRedirect';
import { FiChevronLeft } from 'react-icons/fi';
import { Button, Card, Input, PhoneNumberInput, normalizeUsPhoneNumber } from '@/components';

function SignInPage() {
  const { signIn, setActive, isLoaded } = useSignIn();
  const { isSignedIn, isLoaded: authLoaded } = useAuth();
  const { user, isLoaded: userLoaded } = useUser();
  const navigate = useNavigate();

  // Track screen view
  useEffect(() => {
    track('Sign In Screen');
  }, []);

  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [pendingCode, setPendingCode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Redirect after sign-in (admin phone gets portal choice)
  useEffect(() => {
    if (!authLoaded || !isSignedIn) return;

    const phoneFromForm = normalizeUsPhoneNumber(phone);
    if (phoneFromForm) {
      navigate({ to: getPostAuthDestination(phoneFromForm) });
      return;
    }

    if (!userLoaded) return;
    const phoneFromClerk = user?.primaryPhoneNumber?.phoneNumber ?? '';
    navigate({ to: getPostAuthDestination(phoneFromClerk) });
  }, [authLoaded, isSignedIn, userLoaded, user, phone, navigate]);

  const codeInputRef = useRef<HTMLInputElement>(null);

  // Auto-focus code input when transitioning to verification step
  useEffect(() => {
    if (pendingCode) {
      const timer = setTimeout(() => {
        codeInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [pendingCode]);

  const verifyCode = useCallback(async () => {
    if (!isLoaded || !signIn) return;
    if (!code.trim()) {
      setError('Please enter the SMS code we sent you.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const attempt = await signIn.attemptFirstFactor({
        strategy: 'phone_code',
        code: code.trim(),
      });

      if (attempt.status === 'complete') {
        // Just set the session active - the useEffect watching isSignedIn will handle navigation
        // This prevents a race condition where we navigate before Clerk updates isSignedIn
        await setActive({ session: attempt.createdSessionId });
      } else {
        console.error('Sign-in not complete:', attempt);
        const status = attempt.status ?? 'unknown';
        setError(`Sign in incomplete. Status: ${status}`);
      }
    } catch (err: unknown) {
      console.error('Verification error:', err);
      const clerkError = err as { errors?: { message?: string }[]; message?: string };
      const message =
        clerkError?.errors?.[0]?.message ??
        clerkError?.message ??
        "That code didn't work. Please try again.";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [code, isLoaded, signIn, setActive]);

  // Auto-submit when code reaches 6 digits
  useEffect(() => {
    if (pendingCode && code.length === 6 && !isSubmitting && isLoaded) {
      verifyCode();
    }
  }, [code, pendingCode, isSubmitting, isLoaded, verifyCode]);

  const requestCode = useCallback(async () => {
    if (!isLoaded || !signIn) {
      console.warn('[SignIn] Clerk not loaded yet');
      return;
    }

    const phoneNumber = normalizeUsPhoneNumber(phone);
    if (!phoneNumber) {
      setError('Please enter a valid phone number.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      console.log('[SignIn] Requesting code for:', phoneNumber);
      const result = await signIn.create({ identifier: phoneNumber });

      const phoneFactor = result.supportedFirstFactors?.find(
        (f): f is typeof f & { phoneNumberId: string } =>
          f.strategy === 'phone_code'
      );

      if (!phoneFactor?.phoneNumberId) {
        console.error('[SignIn] No phone_code factor available');
        setError('Phone verification is not available for this account.');
        return;
      }

      await signIn.prepareFirstFactor({
        strategy: 'phone_code',
        phoneNumberId: phoneFactor.phoneNumberId,
      });

      setPendingCode(true);
    } catch (err: unknown) {
      console.error('[SignIn] Error:', err);
      const clerkError = err as { errors?: { code?: string; message?: string; longMessage?: string }[]; message?: string };
      const clerkErrors = clerkError?.errors;
      const errorCode = clerkErrors?.[0]?.code;
      const longMessage = clerkErrors?.[0]?.longMessage;

      // Handle account not found
      if (errorCode === 'form_identifier_not_found') {
        setError(
          "We couldn't find an account for that phone number. Would you like to create one?"
        );
        return;
      }

      const message =
        longMessage ??
        clerkErrors?.[0]?.message ??
        clerkError?.message ??
        "Couldn't send a code. Please try again.";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [isLoaded, phone, signIn]);

  // Check if error is "account not found" to show sign-up link
  const isAccountNotFound = error.includes("couldn't find an account");

  return (
    <div className='min-h-screen bg-sky-light'>
      <div className='max-w-md mx-auto px-6 py-8'>
        {/* Back button */}
        <Link
          to='/auth'
          className='inline-flex items-center text-gray-900 hover:text-primary mb-6 -ml-2'
        >
          <FiChevronLeft size={24} />
        </Link>

        {/* Title */}
        <div className='text-center mb-8'>
          <h1
            className='text-3xl font-semibold text-gray-900 mb-3'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            {pendingCode ? 'Enter your code' : 'Welcome back'}
          </h1>
          <p
            className='text-lg text-gray-500'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            {pendingCode
              ? 'We sent a 6-digit code to your phone.'
              : 'Sign in with your phone number.'}
          </p>
        </div>

        {/* Card */}
        <Card>
          {!pendingCode ? (
            <>
              <PhoneNumberInput
                label='Phone number'
                value={phone}
                onChangeText={setPhone}
                disabled={isSubmitting}
                autoFocus
              />

              {error && (
                <div className='mt-3'>
                  <p className='text-sm text-red-500 text-center'>{error}</p>
                  {isAccountNotFound && (
                    <div className='mt-2 text-center'>
                      <Link
                        to='/auth/sign-up'
                        className='text-primary font-semibold hover:underline'
                      >
                        Create an account
                      </Link>
                    </div>
                  )}
                </div>
              )}

              <div className='mt-4'>
                <Button
                  fullWidth
                  disabled={!isLoaded || isSubmitting}
                  loading={isSubmitting}
                  onClick={requestCode}
                >
                  Send code
                </Button>
              </div>
            </>
          ) : (
            <>
              <Input
                ref={codeInputRef}
                label='Code'
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder='123456'
                type='text'
                inputMode='numeric'
                maxLength={6}
                disabled={isSubmitting}
                autoFocus
              />

              {error && (
                <p className='mt-3 text-sm text-red-500 text-center'>{error}</p>
              )}

              <div className='mt-4'>
                <Button
                  fullWidth
                  disabled={!isLoaded || isSubmitting}
                  loading={isSubmitting}
                  onClick={verifyCode}
                >
                  Continue
                </Button>
              </div>

              <div className='mt-3'>
                <Button
                  variant='secondary'
                  fullWidth
                  disabled={isSubmitting}
                  onClick={() => {
                    setPendingCode(false);
                    setCode('');
                    setError('');
                  }}
                >
                  Use a different number
                </Button>
              </div>
            </>
          )}
        </Card>

        <div className='text-center mt-6 mb-3'>
          <Link
            to='/auth/pro/sign-in'
            className='text-sm text-primary font-semibold hover:underline'
          >
            Lawyer, mediator, or coach? Sign in here
          </Link>
        </div>

        {/* Footer link */}
        <div className='flex items-center justify-center gap-2 mt-2'>
          <span className='text-gray-500'>New here?</span>
          <Link to='/auth/sign-up' className='text-gray-900 font-bold hover:underline'>
            Create an account
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/auth/sign-in')({
  component: SignInPage,
});
